import * as Sentry from '@sentry/nextjs';
import useCharger from 'hooks/useCharger';
import type useGetLoadBalancingGroup from 'hooks/useGetLoadBalancingGroup';
import type { TFunction } from 'next-i18next';
import { useMemo } from 'react';
import { shallowEqual } from 'react-redux';

import type {
  ChargerFullStatusReading,
  LoadBalancingTotalAmpere,
  LoadBalancingTotalAmpereObject,
  LoadMapping,
  Phase,
} from 'redux-store/slices/api/types';
import type { DeviceSerial } from 'redux-store/slices/ui/types';

export interface ParsedLoadBalancingData {
  chg: [
    string,
    number,
    number,
    number,
    number,
    [Phase, Phase, Phase],
    number,
  ][];
}

export interface ChargerData {
  serial: DeviceSerial;
  limit: number;
  chargingCurrent: number;
  priority: number;
  maximumFallbackCurrent: number;
  phaseAssignments: LoadMapping;
  maxConfiguredCurrent: number;
  maximumTotalCurrent?: number;
  maximumGridCurrent?: number;
}

export interface LoadBalancingData {
  chg: ChargerData[];
}

export interface StatusWithSerial {
  serial: string;
  status: NonNullable<ChargerFullStatusReading>;
}

export enum LoadBalancingDataKeys {
  serial,
  limit,
  chargingCurrent,
  priority,
  maximumFallbackCurrent,
  phaseAssignments,
  maxConfiguredCurrent,
}

export const useLoadBalancingData = (
  serial?: DeviceSerial,
): LoadBalancingData | null => {
  const loadBalancingStatusRaw = useCharger(serial, state => state?.los);

  const loadBalancingTotalAmp = useCharger(
    serial,
    state => state?.lot,
    shallowEqual,
  ) as LoadBalancingTotalAmpereObject | undefined;

  return useMemo(() => {
    if (!loadBalancingStatusRaw) return null;

    try {
      const loadBalancingStatusParsed = JSON.parse(
        loadBalancingStatusRaw,
      ) as ParsedLoadBalancingData;

      const maximumTotalCurrent = loadBalancingTotalAmp
        ? loadBalancingTotalAmp.sta ?? loadBalancingTotalAmp.amp
        : undefined;
      const maximumGridCurrent = loadBalancingTotalAmp?.dyn;

      const mappedChg = loadBalancingStatusParsed.chg.map(
        (chg): ChargerData => {
          return {
            serial: chg[LoadBalancingDataKeys.serial],
            limit: chg[LoadBalancingDataKeys.limit],
            chargingCurrent: chg[LoadBalancingDataKeys.chargingCurrent],
            priority: chg[LoadBalancingDataKeys.priority],
            maximumFallbackCurrent:
              chg[LoadBalancingDataKeys.maximumFallbackCurrent],
            phaseAssignments: chg[LoadBalancingDataKeys.phaseAssignments],
            maxConfiguredCurrent:
              chg[LoadBalancingDataKeys.maxConfiguredCurrent],
            ...(maximumTotalCurrent !== undefined && { maximumTotalCurrent }),
            ...(maximumGridCurrent !== undefined && { maximumGridCurrent }),
          };
        },
      );

      return { chg: mappedChg };
    } catch (e) {
      console.error('parse error', e);
      Sentry.captureException(e, {
        tags: { hook: 'useLoadBalancingData' },
      });

      return null;
    }
  }, [loadBalancingStatusRaw, loadBalancingTotalAmp]);
};

export const validateGroupIDChecksum = (groupID: string): boolean => {
  if (groupID.length < 8) {
    return false;
  }

  let checksum = 0;
  for (let i = 0; i < 7; i++) {
    checksum += groupID.charCodeAt(i);
  }

  return groupID.charCodeAt(7) - 65 === checksum % 26;
};

export const generateGroupID = (): string => {
  let groupId = '';
  for (let l = 0; l < 7; l++) {
    groupId += String.fromCharCode(65 + Math.floor(26 * Math.random()));
  }

  let n = 0;
  for (let l = 0; l < 7; l++) {
    n += groupId.charCodeAt(l);
  }

  groupId += String.fromCharCode(65 + (n % 26));

  return groupId;
};

export const formatPhaseAssignments = (
  phaseAssignments?: LoadMapping,
): string =>
  phaseAssignments
    ? phaseAssignments.map(v => (v ? `L${v}` : '-')).join(' ')
    : '';

export const formatAmpere = (
  value: number | undefined,
  t: TFunction,
  longFormat: boolean = false,
): string | undefined | null => {
  if (typeof value !== 'number') return value;

  const key = longFormat
    ? 'common:ampere_with_value_long'
    : 'common:ampere_with_value';

  return t(key, { value: Math.round(value) });
};

export const splitGroupId = (value: string): { name: string; id: string } => {
  const index = value.indexOf('-');

  if (index === -1) return { name: '', id: value };

  return {
    id: value.slice(0, index),
    name: value.slice(index + 1),
  };
};

export interface GroupLoadBalancingData {
  loadBalancingGroupId: string;
  maxTotalCurrent?: number;
  maxGridCurrent?: number;
}

export type LoadBalancingGroup = ReturnType<
  typeof useGetLoadBalancingGroup
>[number];

export type GroupData = LoadBalancingGroup['data'];

export const formatLoadBalancingData = ({
  loadBalancingGroupId,
  loadBalancingData,
}: GroupData): GroupLoadBalancingData => {
  const isDetailed = typeof loadBalancingData === 'object';

  return {
    loadBalancingGroupId,
    maxTotalCurrent: isDetailed ? loadBalancingData.sta : loadBalancingData,
    maxGridCurrent: isDetailed ? loadBalancingData.dyn : undefined,
  };
};

const getLoadBalancingTimestamp = (
  lot: LoadBalancingTotalAmpere | undefined,
): number | undefined => (typeof lot === 'object' ? lot.ts : undefined);

const findChargerWithLatestTimestamp = <T extends StatusWithSerial>(
  chargers: T[],
): T | undefined => {
  let latestCharger: T | undefined = undefined;
  let latestTimestamp: number | undefined = undefined;

  for (const charger of chargers) {
    const timestamp = getLoadBalancingTimestamp(charger.status.lot);

    if (
      timestamp !== undefined &&
      (latestTimestamp === undefined || timestamp > latestTimestamp)
    ) {
      latestCharger = charger;
      latestTimestamp = timestamp;
    }
  }

  return latestCharger;
};

export const selectLoadBalancingCharger = <T extends StatusWithSerial>(
  chargers: T[],
): T | undefined => {
  const enabledChargers = chargers.filter(charger =>
    Boolean(charger.status.loe),
  );

  return (
    findChargerWithLatestTimestamp(enabledChargers) ??
    enabledChargers.at(0) ??
    findChargerWithLatestTimestamp(chargers) ??
    chargers[0]
  );
};

export interface LotData {
  maxTotalCurrent?: number;
  maxGridCurrent?: number;
}

export const getLoadBalancingData = (
  lot: LoadBalancingTotalAmpere | undefined,
): LotData => {
  if (typeof lot === 'object') {
    return { maxTotalCurrent: lot.sta, maxGridCurrent: lot.dyn };
  }

  return { maxTotalCurrent: lot, maxGridCurrent: undefined };
};
